import type { StrategyStatus } from "@/api/types";
import styles from "./Strategies.module.css";

/** The status vocabulary shared by the picker and the strategy bar:
 *  Running N (green, pulsing) · Pending N (amber ring) · Watching · Paused ·
 *  Stalled · Retired · Offline. A strategy with both positions and resting
 *  orders shows both pills. */
export function StatusPills({ status, compact = false }: { status: StrategyStatus; compact?: boolean }) {
  const { state, running, pending } = status;
  if (state === "paused") {
    return (
      <span className={`${styles.pill} ${styles.pillMuted}`}>
        <svg viewBox="0 0 24 24" className={styles.pauseIcon} aria-hidden>
          <rect x="6" y="5" width="4" height="14" />
          <rect x="14" y="5" width="4" height="14" />
        </svg>
        Paused
      </span>
    );
  }
  if (state === "stalled") {
    return (
      <span className={`${styles.pill} ${styles.pillStalled}`}>
        <span className={styles.dot} />
        Stalled
      </span>
    );
  }
  if (state === "retired" || state === "offline" || state === "off") {
    return (
      <span className={`${styles.pill} ${styles.pillMuted}`}>
        {state === "retired" ? "Retired" : state === "off" ? "Not enabled" : "Offline"}
      </span>
    );
  }
  const out = [];
  if (running > 0) {
    out.push(
      <span key="r" className={`${styles.pill} ${styles.pillRunning}`}>
        <span className={styles.dot} />
        {compact ? running : `Running ${running}`}
      </span>,
    );
  }
  if (pending > 0) {
    out.push(
      <span key="p" className={`${styles.pill} ${styles.pillPending}`}>
        <span className={styles.dot} />
        {compact ? pending : `Pending ${pending}`}
      </span>,
    );
  }
  if (out.length === 0) {
    out.push(
      <span key="w" className={`${styles.pill} ${styles.pillWatching}`}>
        <span className={styles.dot} />
        {compact ? "" : "Watching"}
      </span>,
    );
  }
  return <>{out}</>;
}

/** Tiny cumulative-P&L line from the 30-day equity points. */
export function Sparkline({ points, net }: { points: [number, number][]; net: number }) {
  const W = 96;
  const H = 30;
  const ys = [0, ...points.map((p) => p[1])];
  if (ys.length < 2) {
    return (
      <svg className={styles.spark} viewBox={`0 0 ${W} ${H}`} aria-hidden>
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} className={styles.sparkFlat} />
      </svg>
    );
  }
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const span = max - min || 1;
  const d = ys
    .map((y, i) => `${((i / (ys.length - 1)) * W).toFixed(1)},${(H - 3 - ((y - min) / span) * (H - 6)).toFixed(1)}`)
    .join(" ");
  return (
    <svg className={styles.spark} viewBox={`0 0 ${W} ${H}`} aria-hidden>
      <polyline points={d} className={net >= 0 ? styles.sparkUp : styles.sparkDown} />
    </svg>
  );
}
